import { useEffect, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { UserPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PageHero } from '@/components/layout/PageHero';
import { isAdmin as isAdminUser } from '@/features/auth/api';
import { useMe } from '@/features/auth/hooks';
import { useTeamMembers } from '@/features/team/hooks';
import { TeamMembersTable } from '@/features/team/components/TeamMembersTable';
import { TeamInviteDialog } from '@/features/team/components/TeamInviteDialog';
import { TableSkeleton } from '@/components/TableSkeleton';

/**
 * /team — team management page (admins + viewers see the list; only
 * admins see the action affordances).
 *
 * Layout mirrors product-account: hero strip on top with the page
 * title + an "Invite teammate" button (admins only) wired to
 * ``TeamInviteDialog``; below, the ``TeamMembersTable`` lists active
 * members + pending invites in one place. Status + role badges plus
 * a per-row actions menu give admins everything they need without
 * leaving the page.
 *
 * Self-protection (can't change own role / remove self), founder
 * protection (can't change owner role / remove owner), and last-admin
 * protection are enforced in BOTH the backend service AND the table UI
 * — the menu hides for self + founder rows, and the backend returns
 * 400 if anyone bypasses (curl etc.).
 */
export const Route = createFileRoute('/team')({
  component: TeamPage,
});

function TeamPage() {
  const me = useMe();
  const navigate = useNavigate();
  const isAdmin = isAdminUser(me.data);
  // Editors + viewers don't belong here. ``/api/team/members`` returns
  // 403 for them server-side anyway (defense in depth), but bounce them
  // out of the route entirely so they never see a half-rendered shell.
  useEffect(() => {
    if (me.data && !isAdmin) {
      void navigate({ to: '/dashboard', replace: true });
    }
  }, [me.data, isAdmin, navigate]);
  const members = useTeamMembers();
  const [inviteOpen, setInviteOpen] = useState(false);

  return (
    <>
      <PageHero
        title="Team"
        subtitle={
          <>
            Manage who has access to your customer&rsquo;s data. Admins manage
            editors, reviewers, and viewers; only the owner can grant, demote,
            or remove admins. Active members&rsquo; audit history stays attached
            after removal.
          </>
        }
      />

      <main className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 pt-8 pb-20">
        {isAdmin && (
          <div className="flex justify-end mb-4">
            <Button variant="brand" onClick={() => setInviteOpen(true)}>
              <UserPlus className="size-3.5" /> Invite teammate
            </Button>
          </div>
        )}

        {members.isLoading && <TableSkeleton rows={3} label="Loading team" />}

        {members.isError && (
          <div className="bg-state-error-bg border border-state-error-border rounded-2xl p-8 text-state-error-text">
            Could not load the team. Try refreshing the page.
          </div>
        )}

        {members.isSuccess && (
          <TeamMembersTable
            members={members.data}
            currentUserRoles={me.data?.roles}
            currentUserIsOwner={me.data?.is_founder ?? false}
          />
        )}
      </main>

      <TeamInviteDialog
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        currentUserIsOwner={me.data?.is_founder ?? false}
      />
    </>
  );
}
